import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLogoutMutation, useMeQuery } from '@/shared/services/session/session.api';
import { Avatar, AvatarFallback } from '@/shared/components/ui/Avatar/Avatar';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/DropdownMenu/DropdownMenu';
import { selectRefreshToken } from '@/shared/store/reducers/auth/auth.slice';
import { useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

/** First letter of the email — the account has no display name of its own yet. */
function initialOf(email: string | undefined): string {
  return email === undefined || email === '' ? '—' : email.slice(0, 1).toUpperCase();
}

/**
 * Who is signed in, and the way out.
 *
 * The identity comes from `GET /me` rather than from the token: the access token
 * carries authorization state, not an email, and after a reload the store holds
 * only tokens. One cached query is cheaper than persisting a user object that
 * would then need invalidating.
 *
 * Sign-out sends the refresh token so the server can revoke it. Read from the
 * store at click time, never closed over from render — a rotated token would
 * otherwise be presented and read as reuse.
 */
export function UserMenu() {
  const { t } = useTranslation('common');
  const { data: user } = useMeQuery();
  const refreshToken = useAppSelector(selectRefreshToken);
  const [logout, { isLoading }] = useLogoutMutation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('shell.account', { ns: 'uiKit' })}>
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-caption">{initialOf(user?.email)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>
          {/* LTR: an email address is not localised, and letting it inherit RTL
              puts its dot-separated tail in the wrong order. */}
          <span dir="ltr" className="block truncate">
            {user?.email ?? t('state.unknown')}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          tone="danger"
          disabled={isLoading}
          onSelect={() => {
            if (refreshToken === null) return;
            void logout({ refreshToken });
          }}
        >
          <LogOut aria-hidden className="h-4 w-4" />
          {t('action.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
